/**
 * 動的グループのルールを組み立てる(クエリビルダー)。
 *
 * ルールは JSON で、**式言語は作らない**:
 *
 *     {"op": "and"|"or", "conds": [ {field, cmp, value} | 入れ子 ]}
 *
 * 使える field と cmp は `GET /api/hr/groups/fields` が返すものだけ。画面に
 * 埋め込むと API 側と食い違うので、**選択肢はすべて引数の `fields` から作る**。
 * 値の形(数値 / 文字列 / 一覧)は field ごとに固定で、外すとサーバーが 400 で弾く。
 *
 * 「下部を含む」「この rank 以上」は cmp の言い換え(`under` / `rank_gte`)。
 * rank は**小さいほど上位**なので、`rank_gte` は「同格から下」を集める。
 */
import { DeleteOutlined } from "@ant-design/icons";
import { useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Segmented,
  Select,
  Space,
  Tooltip,
  TreeSelect,
  Typography,
} from "antd";

import type { GroupCond, GroupFields, GroupRuleNode } from "../../lib/api";
import { condCount, emptyRule, isNode } from "./groupRule";
import { unitOptions } from "./hooks";

const { Text } = Typography;

function newCond(fields: GroupFields): GroupCond {
  const f = fields.fields[0];
  return { field: f?.key ?? "org_unit", cmp: f?.cmps[0]?.key ?? "eq", value: 0 };
}

/** field を変えたら cmp と value も作り直す(組み合わせを壊さない)。 */
function resetCond(fields: GroupFields, field: string): GroupCond {
  const cmp = fields.fields.find((f) => f.key === field)?.cmps[0]?.key ?? "eq";
  if (field === "status") return { field, cmp, value: [] };
  if (field === "email") return { field, cmp, value: [] };
  if (field === "employment_type") return { field, cmp, value: fields.employment_types[0] ?? "" };
  return { field, cmp, value: 0 };
}

const parseEmails = (text: string) =>
  text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * メールアドレスの一覧(1 行 1 件、"," 区切りも可)。
 * 打っている途中の文字列は手元に持つ。配列から毎回組み立て直すと、Enter で入れた
 * 空行が即座に消えて 2 行目が打てない(HIDE 2026-11-02)。
 */
function EmailListInput({
  value,
  onChange,
  disabled,
  max,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  disabled: boolean;
  max: number;
}) {
  const [text, setText] = useState(() => value.join("\n"));
  // 外から値が変わったとき(読み込み直し・リセット)だけ手元の文字列を合わせる
  const joined = value.join("\n");
  if (parseEmails(text).join("\n") !== joined) setText(joined);
  return (
    <Input.TextArea
      style={{ width: 360 }}
      disabled={disabled}
      autoSize={{ minRows: 3, maxRows: 12 }}
      placeholder={`メールアドレスを 1 行に 1 件(${max} 件まで)`}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseEmails(e.target.value));
      }}
    />
  );
}

function CondRow({
  cond,
  fields,
  disabled,
  onChange,
  onRemove,
}: {
  cond: GroupCond;
  fields: GroupFields;
  disabled: boolean;
  onChange: (c: GroupCond) => void;
  onRemove: () => void;
}) {
  const units = unitOptions(fields.org_units);
  const set = (patch: Partial<GroupCond>) => onChange({ ...cond, ...patch });

  let control = null;
  if (cond.field === "org_unit") {
    control = (
      <Space wrap>
        <TreeSelect
          style={{ width: 260 }}
          disabled={disabled}
          treeDefaultExpandAll
          placeholder="組織を選ぶ"
          value={typeof cond.value === "number" && cond.value > 0 ? cond.value : undefined}
          treeData={units}
          onChange={(v: number) => set({ value: v })}
        />
        <Checkbox
          disabled={disabled}
          checked={cond.cmp === "under"}
          onChange={(e) => set({ cmp: e.target.checked ? "under" : "eq" })}
        >
          下部を含む
        </Checkbox>
      </Space>
    );
  } else if (cond.field === "position") {
    const byRank = cond.cmp === "rank_gte";
    const value = byRank
      ? fields.positions.find((p) => p.rank === cond.value)?.id
      : typeof cond.value === "number" && cond.value > 0
        ? cond.value
        : undefined;
    control = (
      <Space wrap>
        <Select
          style={{ width: 260 }}
          disabled={disabled}
          placeholder="役職を選ぶ"
          value={value}
          options={fields.positions.map((p) => ({
            value: p.id,
            label: `${p.name}(rank ${p.rank})${p.active ? "" : "(停止中)"}`,
          }))}
          onChange={(v: number) => {
            const p = fields.positions.find((x) => x.id === v);
            set({ value: byRank ? (p?.rank ?? 0) : v });
          }}
        />
        <Tooltip title="rank は小さいほど上位。「この rank 以上」は同格から下を集める">
          <Checkbox
            disabled={disabled}
            checked={byRank}
            onChange={(e) => {
              const on = e.target.checked;
              const p = fields.positions.find((x) =>
                on ? x.id === cond.value : x.rank === cond.value,
              );
              set({ cmp: on ? "rank_gte" : "eq", value: on ? (p?.rank ?? 0) : (p?.id ?? 0) });
            }}
          >
            この rank 以上
          </Checkbox>
        </Tooltip>
      </Space>
    );
  } else if (cond.field === "employment_type") {
    control = (
      <Space wrap>
        <Select
          style={{ width: 90 }}
          disabled={disabled}
          value={cond.cmp}
          options={[
            { value: "eq", label: "=" },
            { value: "ne", label: "≠" },
          ]}
          onChange={(v: string) => set({ cmp: v })}
        />
        <Select
          style={{ width: 200 }}
          disabled={disabled}
          placeholder="契約形態"
          value={typeof cond.value === "string" && cond.value ? cond.value : undefined}
          options={fields.employment_types.map((t) => ({ value: t, label: t }))}
          onChange={(v: string) => set({ value: v })}
        />
      </Space>
    );
  } else if (cond.field === "status") {
    control = (
      <Select
        mode="multiple"
        style={{ width: 360 }}
        disabled={disabled}
        placeholder="在籍状態(複数選べる)"
        value={Array.isArray(cond.value) ? cond.value : []}
        options={fields.statuses.map((s) => ({ value: s, label: s }))}
        onChange={(v: string[]) => set({ value: v })}
      />
    );
  } else {
    // email。1 行 1 件(区切りの "," も許す)。件数の上限はサーバーが弾く
    control = (
      <EmailListInput
        disabled={disabled}
        max={fields.limits.emails}
        value={Array.isArray(cond.value) ? cond.value.map(String) : []}
        onChange={(v) => set({ value: v })}
      />
    );
  }

  return (
    <Flex gap={8} align="flex-start" wrap>
      <Select
        style={{ width: 150 }}
        disabled={disabled}
        value={cond.field}
        options={fields.fields.map((f) => ({ value: f.key, label: f.label }))}
        onChange={(v: string) => onChange(resetCond(fields, v))}
      />
      {control}
      {!disabled && (
        <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={onRemove} />
      )}
    </Flex>
  );
}

export function GroupRuleEditor({
  value,
  fields,
  onChange,
  disabled = false,
  depth = 1,
  onRemove,
  total,
}: {
  value: GroupRuleNode;
  fields: GroupFields;
  onChange: (v: GroupRuleNode) => void;
  disabled?: boolean;
  depth?: number;
  onRemove?: () => void;
  /** 一番外側から数えた条件の総数。上限に達したら追加を止める */
  total?: number;
}) {
  const count = total ?? condCount(value);
  const full = count >= fields.limits.conds;
  const deep = depth >= fields.limits.depth;

  const replace = (i: number, c: GroupCond | GroupRuleNode) =>
    onChange({ ...value, conds: value.conds.map((x, j) => (j === i ? c : x)) });
  const remove = (i: number) =>
    onChange({ ...value, conds: value.conds.filter((_, j) => j !== i) });

  return (
    <Card
      size="small"
      style={{ background: depth % 2 === 0 ? "#fafafa" : undefined }}
      title={
        <Space>
          <Segmented
            size="small"
            disabled={disabled}
            value={value.op}
            onChange={(v) => onChange({ ...value, op: v as "and" | "or" })}
            options={[
              { value: "and", label: "すべて満たす(AND)" },
              { value: "or", label: "どれか満たす(OR)" },
            ]}
          />
          {value.conds.length === 0 && <Text type="secondary">条件がまだ無い</Text>}
        </Space>
      }
      extra={
        !disabled &&
        onRemove && (
          <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={onRemove}>
            このまとまりを消す
          </Button>
        )
      }
    >
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {value.conds.map((c, i) =>
          isNode(c) ? (
            <GroupRuleEditor
              key={i}
              value={c}
              fields={fields}
              disabled={disabled}
              depth={depth + 1}
              total={count}
              onChange={(v) => replace(i, v)}
              onRemove={() => remove(i)}
            />
          ) : (
            <CondRow
              key={i}
              cond={c}
              fields={fields}
              disabled={disabled}
              onChange={(v) => replace(i, v)}
              onRemove={() => remove(i)}
            />
          ),
        )}
        {!disabled && (
          <Space wrap>
            <Button
              size="small"
              disabled={full}
              onClick={() => onChange({ ...value, conds: [...value.conds, newCond(fields)] })}
            >
              条件を追加
            </Button>
            <Button
              size="small"
              disabled={full || deep}
              onClick={() => onChange({ ...value, conds: [...value.conds, emptyRule()] })}
            >
              まとまりを追加
            </Button>
            {full && <Text type="secondary">条件は合計 {fields.limits.conds} 件まで</Text>}
            {deep && <Text type="secondary">入れ子は {fields.limits.depth} 段まで</Text>}
          </Space>
        )}
      </Space>
    </Card>
  );
}
